import { useState, useCallback } from 'react';
import axiosInstance from '../services/axiosInstance';

// Hook simple pour le cache de recherche de patients
export function useCachedPatientSearch() {
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cache simple avec localStorage
  const getCacheKey = useCallback((query: string) => `patient_search_${query}`, []);

  const getCachedSearch = useCallback((query: string) => {
    try {
      if (!query) return null;
      const cached = localStorage.getItem(getCacheKey(query));
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  }, [getCacheKey]);

  const setCachedSearch = useCallback((query: string, data: any[]) => {
    try {
      localStorage.setItem(getCacheKey(query), JSON.stringify(data));
      // Expirer après 5 minutes
      setTimeout(() => {
        localStorage.removeItem(getCacheKey(query));
      }, 5 * 60 * 1000);
    } catch {
      // Ignorer les erreurs de localStorage
    }
  }, [getCacheKey]);

  const searchPatients = useCallback(async (query: string) => {
    if (!query || query.length < 3) {
      setResults([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Vérifier le cache d'abord
      const cached = getCachedSearch(query);
      if (cached) {
        setResults(cached);
        return;
      }

      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const response = await axiosInstance.get(
        `/patients/search?query=${encodeURIComponent(query)}&size=5`
      );

      const patients = response.data.content || [];
      const scoredPatients = patients.map((patient: any) => {
        // Calcul de score de pertinence
        let score = 0;
        const fullName = `${patient.firstName} ${patient.lastName}`.toLowerCase();
        const searchLower = query.toLowerCase();

        // Score exact : 100 points
        if (fullName === searchLower) score = 100;
        // Nom complet contient : 80 points  
        else if (fullName.includes(searchLower)) score = 80;
        // Prénom ou nom exact : 70 points
        else if (patient.firstName?.toLowerCase() === searchLower ||
          patient.lastName?.toLowerCase() === searchLower) score = 70;
        // Contient dans prénom ou nom : 50 points
        else if (patient.firstName?.toLowerCase().includes(searchLower) ||
          patient.lastName?.toLowerCase().includes(searchLower)) score = 50;
        // Email ou téléphone : 60 points

        return {
          id: patient.id,
          type: 'patient' as const,
          title: fullName,
          subtitle: `Patient #${patient.id}`,
          details: patient.phone || `N/A`,
          url: `/admin/dashboard/patient/view/${patient.id}`,
          score
        };
      });

      // Trier par score de pertinence
      scoredPatients.sort((a: any, b: any) => b.score - a.score);

      setResults(scoredPatients);
      setCachedSearch(query, scoredPatients);

    } catch (err) {
      setError("Erreur lors de la recherche de patients");
      console.error("Error searching patients:", err);
    } finally {
      setLoading(false);
    }
  }, [getCachedSearch, setCachedSearch]);

  const refetch = useCallback((query: string) => {
    // Invalider le cache et recharger
    localStorage.removeItem(getCacheKey(query));
  }, [getCacheKey]);

  return { results, loading, error, searchPatients, refetch };
}
